// Matches no longer include player names - each MatchData entry carries only a
// playerId. Names are resolved here, in batches of at most 200 ids.
//
// Every rendered row asks for its name as it mounts (see player_name.vue).
// Requests made within the same short window are collected into one batch, so a
// page of matches costs a single round trip covering everything on it, and
// loading more matches costs one more for the ids that are actually new.
import { reactive } from 'vue'
import api_communication, { PLAYER_NAMES_BATCH_LIMIT } from '@/api_communication'

// Names change rarely, so a resolved name is reused for three days before it is
// looked up again.
const CACHE_TTL_MS = 72 * 60 * 60 * 1000
const STORAGE_KEY = 'kopbuff:playerNames'
// Keeps the stored payload bounded no matter how much browsing happens; the
// least recently fetched names are dropped first.
const MAX_CACHED_NAMES = 5000
// Long enough to collect the ids of everything rendering in one pass, short
// enough to stay invisible.
const BATCH_WINDOW_MS = 40

export const PLAYER_NAME_STATUS = {
  bot: 'bot',
  error: 'error',
  idle: 'idle',
  loading: 'loading',
  missing: 'missing',
  resolved: 'resolved',
}

// Keyed by player id. `names` holds resolved names, `status` holds the lookup
// state for ids we have already asked about. Both are reactive so components
// re-render as batches land.
const store = reactive({
  names: {},
  status: {},
})

// When each name was fetched, so entries expire individually rather than the
// whole cache ageing out together.
const fetchedAt = new Map()

const queue = new Set()
const inFlight = new Set()
let batchTimer = null

const isBot = (playerId) => Number(playerId) <= 0

const readStorage = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    const now = Date.now()
    for (const [id, entry] of Object.entries(JSON.parse(raw))) {
      const [name, savedAt] = entry
      if (!name || now - savedAt > CACHE_TTL_MS) continue

      store.names[id] = name
      store.status[id] = PLAYER_NAME_STATUS.resolved
      fetchedAt.set(Number(id), savedAt)
    }
  } catch (error) {
    // Private mode, disabled storage or a corrupt payload - names simply get
    // fetched again.
    console.warn('Could not read cached player names', error)
  }
}

const writeStorage = () => {
  try {
    let entries = [...fetchedAt.entries()]

    if (entries.length > MAX_CACHED_NAMES) {
      entries.sort((a, b) => b[1] - a[1])
      entries = entries.slice(0, MAX_CACHED_NAMES)
      // Drop the evicted ids so the in-memory and stored views stay in step.
      const kept = new Set(entries.map(([id]) => id))
      for (const id of fetchedAt.keys()) {
        if (!kept.has(id)) fetchedAt.delete(id)
      }
    }

    const payload = {}
    for (const [id, savedAt] of entries) {
      const name = store.names[id]
      if (name) payload[id] = [name, savedAt]
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    console.warn('Could not cache player names', error)
  }
}

const chunk = (ids, size) => {
  const chunks = []
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size))
  }
  return chunks
}

const fetchBatch = async (ids) => {
  ids.forEach((id) => inFlight.add(id))

  try {
    const rows = await api_communication.getPlayerNames(ids)
    const resolved = new Map(rows.map((row) => [Number(row.id), row.name]))
    const now = Date.now()

    ids.forEach((id) => {
      if (resolved.has(id)) {
        store.names[id] = resolved.get(id)
        store.status[id] = PLAYER_NAME_STATUS.resolved
        fetchedAt.set(id, now)
      } else {
        // The API omits ids with no matching account. Not cached: an id with no
        // account today may well have one tomorrow.
        store.status[id] = PLAYER_NAME_STATUS.missing
      }
    })
  } catch (error) {
    console.error('Could not resolve player names', error)
    // Left as an error rather than missing, so a later request can retry.
    ids.forEach((id) => {
      store.status[id] = PLAYER_NAME_STATUS.error
    })
  } finally {
    ids.forEach((id) => inFlight.delete(id))
  }
}

const flush = async () => {
  batchTimer = null

  const ids = [...queue]
  queue.clear()
  if (!ids.length) return

  await Promise.all(chunk(ids, PLAYER_NAMES_BATCH_LIMIT).map(fetchBatch))
  writeStorage()
}

const scheduleFlush = () => {
  if (batchTimer) return
  batchTimer = setTimeout(flush, BATCH_WINDOW_MS)
}

const PlayerNames = {
  /**
   * Queues the given player ids for the next batch. Bots, ids already resolved
   * and ids already being fetched are skipped, so this is safe to call on every
   * render.
   */
  request(playerIds) {
    const ids = (Array.isArray(playerIds) ? playerIds : [playerIds]).map(Number)
    let queued = false

    for (const id of ids) {
      if (!Number.isFinite(id) || isBot(id)) continue
      if (inFlight.has(id) || queue.has(id)) continue

      const status = store.status[id]
      if (status === PLAYER_NAME_STATUS.resolved || status === PLAYER_NAME_STATUS.missing) continue

      store.status[id] = PLAYER_NAME_STATUS.loading
      queue.add(id)
      queued = true
    }

    if (queued) {
      scheduleFlush()
    }
  },

  getName(playerId) {
    return store.names[Number(playerId)] || null
  },

  getStatus(playerId) {
    const id = Number(playerId)
    if (!Number.isFinite(id)) return PLAYER_NAME_STATUS.missing
    if (isBot(id)) return PLAYER_NAME_STATUS.bot
    return store.status[id] || PLAYER_NAME_STATUS.idle
  },

  isBot,
}

readStorage()

export default PlayerNames
